import { useMemo, useState } from "react";
import {
  BellOutlined,
  ControlOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileSearchOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MobileOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SyncOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Dropdown,
  Flex,
  Layout,
  Menu,
  Space,
  Typography,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import type { DashboardSummary } from "@deltabeacon/contracts";
import { useAuth } from "../auth";
import { api } from "../lib/api";
import { time } from "../lib/format";
import { DataStatusTag } from "./common";

const { Header, Sider, Content } = Layout;

const menuItems = [
  { key: "/dashboard", icon: <DashboardOutlined />, label: "运营概览" },
  { key: "/users", icon: <TeamOutlined />, label: "App 用户" },
  { key: "/market", icon: <DatabaseOutlined />, label: "全币种行情" },
  { key: "/alerts", icon: <ControlOutlined />, label: "全局提醒规则" },
  { key: "/system", icon: <SettingOutlined />, label: "系统配置" },
  { key: "/events", icon: <BellOutlined />, label: "提醒事件" },
  { key: "/devices", icon: <MobileOutlined />, label: "推送设备" },
  { key: "/sync", icon: <SyncOutlined />, label: "同步健康" },
  {
    key: "/admins",
    icon: <SafetyCertificateOutlined />,
    label: "管理员与角色",
    superOnly: true,
  },
  { key: "/audit", icon: <FileSearchOutlined />, label: "审计日志" },
];

export function AdminShell() {
  const { admin, loading, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dashboard = useQuery({
    queryKey: ["dashboard-shell"],
    queryFn: () => api<DashboardSummary>("/dashboard"),
    enabled: Boolean(admin),
    refetchInterval: (query) =>
      (query.state.data?.refreshIntervalSeconds ?? 60) * 1000,
  });
  const items = useMemo(
    () =>
      menuItems
        .filter((item) => !item.superOnly || admin?.role === "SUPER_ADMIN")
        .map(({ key, icon, label }) => ({ key, icon, label })),
    [admin?.role],
  );

  if (loading)
    return (
      <div className="app-loader">
        <div className="brand-mark">
          <span />
        </div>
        <span>加载中…</span>
      </div>
    );
  if (!admin)
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  const selected =
    items.find((item) => location.pathname.startsWith(item.key))?.key ??
    "/dashboard";

  return (
    <Layout className="admin-layout">
      <Sider
        width={244}
        collapsedWidth={76}
        collapsed={collapsed}
        className="admin-sider"
      >
        <div className="sider-brand">
          <div className="brand-mark">
            <span />
          </div>
          {!collapsed ? (
            <div>
              <strong>DeltaBeacon</strong>
            </div>
          ) : null}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selected]}
          items={items}
          onClick={({ key }) => navigate(key)}
        />
        <div className="sider-system">
          <span className="pulse-dot" />
          {!collapsed ? <span>行情扫描服务</span> : null}
        </div>
      </Sider>
      <Layout>
        <Header className="admin-header">
          <Flex align="center" justify="space-between" gap={16}>
            <Button
              type="text"
              className="collapse-button"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed((value) => !value)}
            />
            <Space size={18} className="header-health">
              <DataStatusTag
                status={dashboard.data?.dataStatus ?? "UNAVAILABLE"}
              />
              <span>
                <small>刷新周期</small>
                <b className="mono">
                  {dashboard.data?.refreshIntervalSeconds ?? "—"}s
                </b>
              </span>
              <span>
                <small>最近同步</small>
                <b>{time(dashboard.data?.lastSyncedAt)}</b>
              </span>
            </Space>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "profile",
                    icon: <UserOutlined />,
                    label: `${admin.name} · ${admin.role}`,
                    disabled: true,
                  },
                  { type: "divider" },
                  {
                    key: "logout",
                    icon: <LogoutOutlined />,
                    label: "退出登录",
                    danger: true,
                  },
                ],
                onClick: async ({ key }) => {
                  if (key === "logout") await logout();
                },
              }}
            >
              <Button type="text" className="admin-account">
                <Avatar size={30}>{admin.name.slice(0, 1)}</Avatar>
                <span>
                  <b>{admin.name}</b>
                  <small>{admin.email}</small>
                </span>
              </Button>
            </Dropdown>
          </Flex>
        </Header>
        <Content className="admin-content">
          <Outlet />
          <footer className="admin-footer">
            <Typography.Text>
              DeltaBeacon V1 · 行情仅供参考，不构成投资建议
            </Typography.Text>
          </footer>
        </Content>
      </Layout>
    </Layout>
  );
}
